/**
 * PSTUDIO-3574 / PSTUDIO-3602 / PSTUDIO-3992 — node-number policy for pasting a
 * COPIED bundle.
 *
 * Kept pure and dependency-light (mirrors table-clipboard.ts: no store/MobX
 * imports at runtime) so the node-env vitest suite can cover every branch
 * without dragging in the heavy store.
 *
 * Policy (behavior contract — KEYBOARD_CLIPBOARD_BEHAVIOR.md guard rail 11 +
 * Confluence page 5582880772):
 * - RAW mode (autoRenumber=false): ALWAYS PRESERVE — the pasted nodes land with
 *   their source numbers verbatim, EVEN when that creates a duplicate. The
 *   normal duplicate-node-number validation surfaces the error afterwards; the
 *   paste is never blocked (PSTUDIO-3602 — nodes must always paste).
 * - SMART mode (autoRenumber=true): PRESERVE the source node numbers iff every
 *   non-group bundle row carries a valid number, the numbers are unique within
 *   the bundle, and none is occupied in the target; otherwise RENUMBER the
 *   whole bundle and expose the old→new map so the caller can rewrite
 *   intra-bundle refs. A `'replace'` (`isReplace`) never preserves — see below.
 * Cut+paste never renumbers and never reaches this module.
 *
 * PSTUDIO-3992 — the renumber branch allocates numbers LOCAL TO THE INSERTION
 * POINT via the shared insert-time algorithm (`assignNodeNumbers`, PSTUDIO-2871)
 * instead of the old table-wide `getMaxNodeNumber() + 1 + bundleIndex`. A number
 * taken in an unrelated section of the solution no longer drags the paste to a
 * far-away number (Leo/Edan product decision): the bundle lands on the closest
 * free slot next to where it was pasted. `assignNodeNumbers`' own fallback
 * (max + 1) survives for the one case that has no local anchor — pasting above
 * the very first node, where `reference` is null.
 *
 * PSTUDIO-4083 — a SMART `'replace'` ALWAYS renumbers, whatever the bundle
 * carries. Edan's rule of 2026-09-08 is written without a condition on it: "All
 * pasted nodes, no matter how many they are and no matter how many rows I have
 * highlighted, must take on NET NEW node numbers, to be considered the
 * 'children' of the node immediately above." Preserving a free source number
 * contradicted that — measured on `6 · 7 · 8`, replacing node 7 with a clone
 * numbered 1 left the clone on 1 instead of making it 60, which is the answer he
 * gave on the call (50:12). RAW mode is untouched: the toggle is the user
 * switching auto-numbering off, and verbatim still means verbatim there.
 */

import { assignNodeNumbers } from './smart-node-numbering';

/** A usable node number is a positive integer — matches getMaxNodeNumber's
 *  `row.nodeNumber || 0` semantics and the duplicate validator's integer rule.
 *
 *  Deliberately NOT capped at Number.MAX_SAFE_INTEGER. This predicate also
 *  decides what RAW mode preserves, and guard rail 11 says verbatim means
 *  verbatim: rejecting an over-cap number here would silently blank the pasted
 *  row's `Node #` instead of copying it. The ceiling is the ALLOCATOR's problem
 *  (its scans advance with `n++`, which stops working up there), so the guard
 *  lives in `assignNodeNumbers` — see its safe-integer check. Every number a
 *  pasted row receives now comes from that allocator (PSTUDIO-4083 removed the
 *  one path that handed a row a number without it). */
export function toValidNodeNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(num) && num > 0 ? num : undefined;
}

export interface PasteNumberPlan {
  mode: 'preserve' | 'renumber';
  /** old → new node number for non-group rows carrying a valid number.
   *  Empty in 'preserve' mode (numbers don't change). */
  numberMap: Map<number, number>;
  /** PSTUDIO-3992: the allocated numbers, one per NON-GROUP bundle row, in
   *  bundle order — the single source the caller assigns from (group headers
   *  consume no slot, and a row whose source number was invalid still gets one,
   *  which is why this can't be derived from `numberMap` alone). Empty in
   *  'preserve' mode. */
  numbers: number[];
}

export function computePasteNumberPlan(opts: {
  /** Clipboard bundle rows in paste order. Group headers are flagged: they
   *  neither gate preservation nor enter the number map (a group row's own
   *  number is excluded from numbering everywhere — see getMaxNodeNumber). */
  bundle: Array<{ nodeNumber: unknown; isGroupHeader: boolean }>;
  /** Numbers already present on the target table's non-group rows. PSTUDIO-4083:
   *  a `'replace'` paste's dying rows are INCLUDED — they keep owning their
   *  numbers while this runs, so the clones are never handed one of them. */
  occupiedNumbers: ReadonlySet<number>;
  /** PSTUDIO-4083: numbers rows merely POINT AT. They block a collision (so a
   *  bundle carrying one cannot preserve it) but must NOT anchor Fallback's
   *  `max + 1` — see `assignNodeNumbers`. Optional so a caller that has no
   *  reference data keeps the old two-argument shape. */
  referencedNumbers?: ReadonlySet<number>;
  /** PSTUDIO-3992 — the numbering context around the paste slot, resolved by
   *  `tableStore.resolveNumberingContext`. `reference` is the node directly
   *  ABOVE the insertion point (null when pasting above the very first node),
   *  `upper` the node directly BELOW it (null when appending at the end).
   *  Same meaning as the insert path (NODE_NUMBERING_BEHAVIOR.md §2). */
  reference: number | null;
  upper: number | null;
  /** PSTUDIO-4083 — true when the paste REPLACES the rows it lands on. A smart
   *  replace never preserves the bundle's own numbers: the clones are children
   *  of the row above the slot, so they take net new numbers even when the
   *  source numbers are free. Ignored in RAW mode, which preserves verbatim by
   *  design. Defaults to false so every other paste position is unaffected. */
  isReplace?: boolean;
  /** PSTUDIO-3602 — the "Auto-renumber pasted nodes" toggle.
   *  true  (Smart, default): preserve when free, else renumber the bundle +
   *  rewrite refs on collision.
   *  false (Raw): always preserve verbatim — the nodes paste EVEN when that
   *  creates a duplicate number; the normal validation flags it (no block). */
  autoRenumber: boolean;
}): PasteNumberPlan {
  const {
    bundle,
    occupiedNumbers,
    referencedNumbers,
    reference,
    upper,
    autoRenumber,
    isReplace = false,
  } = opts;

  // RAW (toggle OFF): always paste the source numbers verbatim. PSTUDIO-3602 —
  // a paste must ALWAYS succeed even when it produces a duplicate node number;
  // the duplicate is surfaced by the normal duplicate-node-number validation,
  // never blocked here. Only SMART renumbers to keep numbers unique.
  if (!autoRenumber) {
    return { mode: 'preserve', numberMap: new Map(), numbers: [] };
  }

  // SMART (toggle ON): preserve the source numbers when every non-group number
  // is valid, unique within the bundle and free in the target; otherwise
  // renumber the whole bundle and expose the old→new map (PSTUDIO-3574).
  const nodeNumbers = bundle
    .filter((row) => !row.isGroupHeader)
    .map((row) => toValidNodeNumber(row.nodeNumber));

  const allValid = nodeNumbers.every((num): num is number => num !== undefined);
  const uniqueInBundle = new Set(nodeNumbers).size === nodeNumbers.length;
  const allFree =
    allValid &&
    nodeNumbers.every(
      (num) => !occupiedNumbers.has(num) && !referencedNumbers?.has(num)
    );

  // PSTUDIO-4083: `!isReplace` is what makes a smart replace skip preservation
  // entirely. Without it a bundle whose numbers happen to be free kept them, so
  // the SAME gesture answered "net new" or "verbatim" depending on what the
  // clipboard held — and the contract's promise that a smart replace always
  // renumbers was only true when something collided.
  if (!isReplace && allValid && uniqueInBundle && allFree) {
    return { mode: 'preserve', numberMap: new Map(), numbers: [] };
  }

  // PSTUDIO-3992: allocate ONE number per non-group row, local to the insertion
  // point, through the same algorithm the insert path uses. Group headers are
  // excluded (they stay unnumbered — guard rail 11), so they consume no slot and
  // leave no gap in the allocated block.
  //
  // PSTUDIO-4083: a `'replace'` runs through here unchanged — its whole
  // difference lives in the INPUTS the store hands over (the dying rows are
  // still in `occupiedNumbers` and still bound the slot), which is what makes a
  // replace number exactly like an insert above the first replaced row.
  const numbers = assignNodeNumbers(
    reference,
    upper,
    nodeNumbers.length,
    [...occupiedNumbers],
    referencedNumbers ? [...referencedNumbers] : []
  );

  // Map old → new for rows carrying a valid source number, so intra-bundle refs
  // can be rewritten. A number repeated inside the bundle can only map 1:1, so
  // the FIRST occurrence wins the slot (each row still gets its own number).
  const numberMap = new Map<number, number>();
  let slot = 0;
  bundle.forEach((row) => {
    if (row.isGroupHeader) return;
    const newNumber = numbers[slot++];
    const oldNumber = toValidNodeNumber(row.nodeNumber);
    if (oldNumber !== undefined && !numberMap.has(oldNumber)) {
      numberMap.set(oldNumber, newNumber);
    }
  });
  return { mode: 'renumber', numberMap, numbers };
}

/**
 * The shapes a node-number reference can take, in ONE place.
 *
 * PSTUDIO-4083: these used to be inline literals inside the rewriter. They are
 * shared now because a second consumer arrived — `collectReferencedNodeNumbers`
 * — and a reader that recognises fewer shapes than the writer rewrites is a
 * silent hole: a number the collector misses is a number the allocator believes
 * is free and hands to a new node, which then inherits the edges pointing at it.
 * Change a pattern here and both sides move together.
 *
 * Each is built fresh per use (`new RegExp`) rather than shared as a literal,
 * because the `g` flag carries `lastIndex` between calls.
 */
const REF_PATTERNS = {
  /** `value~N` targets: digits right after '~', ended by '|', whitespace or end
   *  of string — so a webview value like `label~https://…/5` is never a target. */
  tildeTarget: () => /~(\d+)(?=\||\s|$)/g,
  /** A whole string that is one number token, padding included. */
  wholeNumber: () => /^(\s*)(\d+)(\s*)$/,
  /** `"dest": "N"` inside a JSON STRING. */
  destJsonQuoted: () => /("dest"\s*:\s*")(\d+)(")/g,
  /** `"dest": N` inside a JSON STRING. */
  destJsonBare: () => /("dest"\s*:\s*)(\d+)/g,
} as const;

/** Swap a pure digit token through the map, else return it unchanged. */
function mapDigitToken(
  digits: string,
  numberMap: ReadonlyMap<number, number>
): string {
  const num = toValidNodeNumber(digits);
  const mapped = num !== undefined ? numberMap.get(num) : undefined;
  return mapped !== undefined ? String(mapped) : digits;
}

/** Rewrite `value~N` targets: only digits immediately after a '~' and
 *  terminated by '|', whitespace or end-of-string (so `label~https://…/5`
 *  style webview values are never touched). */
function rewriteTildeTargets(
  text: string,
  numberMap: ReadonlyMap<number, number>
): string {
  return text.replace(REF_PATTERNS.tildeTarget(), (_match, digits) => {
    const mappedDigits = mapDigitToken(digits as string, numberMap);
    return `~${mappedDigits}`;
  });
}

/** Rewrite one nextNodes entry preserving its scalar type: numbers map to
 *  numbers; a string that is a pure number token (optionally padded) keeps
 *  its padding bytes; anything else is untouched. */
function rewriteNextNodesEntry(
  entry: unknown,
  numberMap: ReadonlyMap<number, number>
): unknown {
  if (typeof entry === 'number') {
    return numberMap.get(entry) ?? entry;
  }
  if (typeof entry === 'string') {
    return entry.replace(
      REF_PATTERNS.wholeNumber(),
      // eslint-disable-next-line max-params
      (_match, pre, digits, post) => {
        const mappedDigits = mapDigitToken(digits as string, numberMap);
        return `${pre as string}${mappedDigits}${post as string}`;
      }
    );
  }
  return entry;
}

/** Depth-first rewrite of every `dest` key inside a parsed rich-asset
 *  content object (options, carousel additional_cta, …), preserving the
 *  original scalar type (number stays number, string stays string). */
function rewriteDestDeep(
  node: unknown,
  numberMap: ReadonlyMap<number, number>
): void {
  if (Array.isArray(node)) {
    node.forEach((child) => rewriteDestDeep(child, numberMap));
    return;
  }
  if (node === null || typeof node !== 'object') return;
  const record = node as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key === 'dest') {
      if (typeof value === 'number') {
        const mapped = numberMap.get(value);
        if (mapped !== undefined) record[key] = mapped;
      } else if (typeof value === 'string') {
        record[key] = value.replace(
          REF_PATTERNS.wholeNumber(),
          // eslint-disable-next-line max-params
          (_match, pre, digits, post) => {
            const mappedDigits = mapDigitToken(digits as string, numberMap);
            return `${pre as string}${mappedDigits}${post as string}`;
          }
        );
      }
    } else {
      rewriteDestDeep(value, numberMap);
    }
  }
}

/** Rewrite `"dest": N` / `"dest": "N"` tokens inside a JSON-string
 *  rich-asset content WITHOUT parsing it — byte-identity everywhere else. */
function rewriteDestInJsonString(
  json: string,
  numberMap: ReadonlyMap<number, number>
): string {
  return (
    json
      // eslint-disable-next-line max-params
      .replace(REF_PATTERNS.destJsonQuoted(), (_match, pre, digits, post) => {
        const mappedDigits = mapDigitToken(digits as string, numberMap);
        return `${pre as string}${mappedDigits}${post as string}`;
      })
      // eslint-disable-next-line max-params
      .replace(REF_PATTERNS.destJsonBare(), (_match, pre, digits) => {
        const mappedDigits = mapDigitToken(digits as string, numberMap);
        return `${pre as string}${mappedDigits}`;
      })
  );
}

/** Collect a `~N` target out of a string. */
function collectTildeTargets(text: string, into: Set<number>): void {
  for (const match of text.matchAll(REF_PATTERNS.tildeTarget())) {
    const num = toValidNodeNumber(match[1]);
    if (num !== undefined) into.add(num);
  }
}

/** Collect one `nextNodes` entry: a number, or a string that is one number. */
function collectNextNodesEntry(entry: unknown, into: Set<number>): void {
  if (typeof entry === 'number') {
    const num = toValidNodeNumber(entry);
    if (num !== undefined) into.add(num);
    return;
  }
  if (typeof entry !== 'string') return;
  const match = REF_PATTERNS.wholeNumber().exec(entry);
  if (!match) return;
  const num = toValidNodeNumber(match[2]);
  if (num !== undefined) into.add(num);
}

/** Depth-first collection of every `dest` inside a parsed rich-asset object. */
function collectDestDeep(node: unknown, into: Set<number>): void {
  if (Array.isArray(node)) {
    node.forEach((child) => collectDestDeep(child, into));
    return;
  }
  if (node === null || typeof node !== 'object') return;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    if (key === 'dest') collectNextNodesEntry(value, into);
    else collectDestDeep(value, into);
  }
}

/** Collect `"dest": N` / `"dest": "N"` out of a JSON-STRING rich asset. */
function collectDestInJsonString(json: string, into: Set<number>): void {
  for (const pattern of [
    REF_PATTERNS.destJsonQuoted(),
    REF_PATTERNS.destJsonBare(),
  ]) {
    for (const match of json.matchAll(pattern)) {
      const num = toValidNodeNumber(match[2]);
      if (num !== undefined) into.add(num);
    }
  }
}

/**
 * Every node number this row POINTS AT — the read-only twin of
 * `rewriteNodeNumberReferences`, walking the same fields through the same
 * `REF_PATTERNS`.
 *
 * PSTUDIO-4083 — why a pointed-at number counts as taken. Deleting a node does
 * NOT rewrite the rows that referenced it (that is deliberate: the dangling edge
 * is meant to surface as a validation error, Edan 2026-09-08). So after deleting
 * node 4, the number 4 is still present in the data, and handing it to a NEW
 * node makes that node silently inherit every edge aimed at the node that died —
 * an error the user can see turns into a graph that is quietly wrong.
 *
 * Deliberately NOT limited to numbers that are missing from the table: a number
 * that IS live is already in the occupied set, so counting it twice costs
 * nothing and removes a reason for the two sides to disagree.
 */
export function collectReferencedNodeNumbers(
  row: { nextNodes?: unknown; whatsNext?: unknown; richAssetContent?: unknown },
  into: Set<number>
): void {
  if (Array.isArray(row.nextNodes)) {
    row.nextNodes.forEach((entry) => collectNextNodesEntry(entry, into));
  } else if (row.nextNodes !== undefined && row.nextNodes !== null) {
    collectNextNodesEntry(row.nextNodes, into);
  }

  if (Array.isArray(row.whatsNext)) {
    row.whatsNext.forEach((pair) => {
      if (typeof pair === 'string') collectTildeTargets(pair, into);
    });
  } else if (typeof row.whatsNext === 'string') {
    collectTildeTargets(row.whatsNext, into);
  }

  const content = row.richAssetContent;
  if (content === undefined || content === null) return;
  if (typeof content === 'object') {
    collectDestDeep(content, into);
    return;
  }
  if (typeof content !== 'string') return;
  const trimmed = content.trimStart();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    collectDestInJsonString(content, into);
  } else {
    collectTildeTargets(content, into);
  }
}

/**
 * Remap every intra-bundle node-number reference on one pasted row through
 * the old→new plan. Mutates the row in place (pasteRow calls it on immer
 * drafts inside produce(), so the mutation is captured immutably and lands in
 * the undo snapshot automatically). References to numbers OUTSIDE the map —
 * i.e. outside the pasted bundle — are never touched, and the rewrite is
 * token-level only (byte-identity contract with the validator/compiler,
 * PSTUDIO-3007).
 */
export function rewriteNodeNumberReferences(
  row: { nextNodes?: unknown; whatsNext?: unknown; richAssetContent?: unknown },
  numberMap: ReadonlyMap<number, number>
): void {
  if (numberMap.size === 0) return;

  if (Array.isArray(row.nextNodes)) {
    row.nextNodes = row.nextNodes.map((entry) =>
      rewriteNextNodesEntry(entry, numberMap)
    );
  } else if (row.nextNodes !== undefined && row.nextNodes !== null) {
    row.nextNodes = rewriteNextNodesEntry(row.nextNodes, numberMap);
  }

  if (Array.isArray(row.whatsNext)) {
    row.whatsNext = row.whatsNext.map((pair) =>
      typeof pair === 'string' ? rewriteTildeTargets(pair, numberMap) : pair
    );
  } else if (typeof row.whatsNext === 'string') {
    row.whatsNext = rewriteTildeTargets(row.whatsNext, numberMap);
  }

  const content = row.richAssetContent;
  if (content !== undefined && content !== null) {
    if (typeof content === 'object') {
      rewriteDestDeep(content, numberMap);
    } else if (typeof content === 'string') {
      const trimmed = content.trimStart();
      row.richAssetContent =
        trimmed.startsWith('{') || trimmed.startsWith('[')
          ? rewriteDestInJsonString(content, numberMap)
          : rewriteTildeTargets(content, numberMap);
    }
  }
}
